/** バックエンドの API contract をそのまま写した型定義です。any は使いません。 */

export type FindingSeverity = 'error' | 'warning' | 'info';
export type DiffType = 'new' | 'update' | 'unchanged';

export type MosPosWebMenu = {
  web_menu_cd: string;
  /** Master CSV 14列目。値が無い web code でも null ではなく [] が返ります。 */
  special_order_item_codes: string[];
};

export type MosPosPricePeriod = {
  sell_price: number;
  reduced_sell_price: number;
  sell_start_date: string;
  sell_end_date: string;
  sell_stop_flag: 0 | 1;
};

export type MosPosAccessory = {
  mets_menu_cd: string;
  web_menu_cds: MosPosWebMenu[];
  mets_screen_menu_name: string;
  prices: MosPosPricePeriod[];
  reduced_tax_rate_flag: number;
  quantity: number;
  display_order: number;
};

export type MosPosComponent = {
  mets_menu_cd: string;
  web_menu_cds: MosPosWebMenu[];
  mets_screen_menu_name: string;
  prices: MosPosPricePeriod[];
  reduced_tax_rate_flag: number;
  quantity: number;
  sell_price_difference?: number;
  reduced_sell_price_difference?: number;
  sell_quantity?: number;
  mets_crust_cd?: string;
  mets_size_cd?: string;
  display_order: number;
  accessories: MosPosAccessory[];
};

export type MosPosChoiceGroup = {
  mets_choice_cd: string;
  choice_group_name: string;
  selectable_items: MosPosComponent[];
};

export type MosPosSetConfig = {
  set_parent_mets_menu_cd: string;
  web_menu_cds: MosPosWebMenu[];
  set_parent_name: string;
  prices: MosPosPricePeriod[];
  set_parent_reduced_tax_rate_flag: number;
  included_items: MosPosComponent[];
  choice_groups: MosPosChoiceGroup[];
};

export type MosPosMenu = {
  mets_menu_cd: string;
  web_menu_cds: MosPosWebMenu[];
  mets_screen_menu_name: string;
  mets_receipt_menu_name?: string;
  menu_type?: number;
  prices: MosPosPricePeriod[];
  subgrouping_cd?: string;
  reduced_tax_rate_flag: number;
  alcohol_flag?: number;
  set_configs: MosPosSetConfig[];
  accessories: MosPosAccessory[];
  source_relation_count: number;
};

export type MosPosMenuSummary = {
  registered_count: number;
  relation_count: number;
  /** 販売開始前（sell_start_date が business_date より後で、まだ販売停止していない）root 件数です。 */
  prepare_count: number;
  active_count: number;
  ended_count: number;
};

export type MosPosMenuListData = {
  business_date: string;
  summary: MosPosMenuSummary;
  menus: MosPosMenu[];
};

export type MosPosFinding = {
  file: 'master' | 'relation';
  row: number;
  menu_code?: string;
  column?: string;
  rule: string;
  severity: FindingSeverity;
  /** ログ用の英語メッセージです。UI 文言としては使わず rule と severity で分岐します。 */
  message: string;
  /**
   * V-014 が指す特殊注文コードです。menu_code は mets_menu_cd を表すため別項目に
   * なっています。message から推測せず、この値をそのまま表示します。
   */
  special_order_code?: string;
};

export type MosPosFileMeta = {
  name: string;
  size_bytes: number;
};

export type MosPosValidateSummary = {
  business_date: string;
  master_rows: number;
  relation_rows: number;
  /** Error があるときは返りません。 */
  documents?: number;
  errors: number;
  warnings: number;
  infos: number;
};

export type MosPosDiff = {
  new: number;
  /** 既存キー全件。unchanged を含むため documents = new + update になります。 */
  update: number;
  unchanged: number;
};

export type MosPosValidateData = {
  files: {
    master: MosPosFileMeta;
    relation: MosPosFileMeta;
  };
  summary: MosPosValidateSummary;
  diff?: MosPosDiff;
  findings: MosPosFinding[];
  findings_truncated: boolean;
  preview?: {
    menus: MosPosMenu[];
    /** mets_menu_cd をキーにした取込区分。行ごとのバッジ表示に使います。 */
    diff_types: Record<string, DiffType>;
  };
};

/** validate は business validation の Error でも 200 + ok:false を返します。 */
export type MosPosValidateResponse = {
  ok: boolean;
  data: MosPosValidateData;
};

export type MosPosImportResult = {
  new: number;
  update: number;
  unchanged: number;
  master_rows: number;
  relation_rows: number;
  business_date: string;
};

export type MosPosErrorCode =
  | 'POS_MENU_IMPORT_INVALID_REQUEST'
  | 'POS_MENU_IMPORT_TOO_LARGE'
  | 'POS_MENU_IMPORT_VALIDATION_FAILED'
  | 'POS_MENU_IMPORT_ROLLED_BACK'
  | 'POS_MENU_IMPORT_RESULT_UNKNOWN'
  | 'POS_MENU_IMPORT_INTERNAL_ERROR'
  | 'POS_MENU_SCHEMA_MIGRATION_REQUIRED';

export type MosPosFailureCode =
  | MosPosErrorCode
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'UNKNOWN';

/** API が返した失敗を status と code のまま保持します。 */
export class MosPosMenuMasterError extends Error {
  constructor(
    readonly code: MosPosFailureCode,
    readonly status: number,
    /** 422 のときだけ findings を含む data が入ります。 */
    readonly validation?: MosPosValidateData
  ) {
    super(code);
    this.name = 'MosPosMenuMasterError';
    Object.setPrototypeOf(this, MosPosMenuMasterError.prototype);
  }
}
